import moment from "moment";
import CSVParser from "papaparse";

/**
 * Formats the URL so as to provide values for the its dynamic sections or
 * query strings. <br>
 * **Example**: `formatUrl("http://www.mydomain.com/{id}/{name}", {"query":"query_value"}, {id:1234, name:"name_value"})`
 * will return `http://www.mydomain.com/1234/name_value?query=query_value`
 * @param {String} endpoint The endpoint url
 * @param {Object} queryStrings Object containing the query string parameters
 * @param {Object} dynamicContent Object containing the values for the dynamic parts of the url
 */
export function formatUrl(endpoint: string, queryStrings?: { [k: string]: string } | null, dynamicContent?: { [k: string]: string }) {
    let qs = "";
    queryStrings = queryStrings || {};
    dynamicContent = dynamicContent || {};

    Object.keys(queryStrings).forEach((key) => {
        if (queryStrings![key]) {
            const value = encodeURIComponent(queryStrings![key]);
            qs = `${qs}${qs.length > 0 ? "&" : ""}${key}=${value}`;
        }
    });

    Object.keys(dynamicContent).forEach((key) => {
        if (dynamicContent![key]) {
            const re = RegExp(`{${key}}`);
            endpoint = endpoint.replace(re, dynamicContent![key]);
        }
    });

    if (qs.length > 0) {
        endpoint = `${endpoint}?${qs}`;
    }

    return endpoint;
}

export function getAge(dateInput: string | moment.Moment | Date) {
    if (!moment(dateInput).isValid()) return "";
    const today = new Date();
    const birthDate = new Date(moment.isMoment(dateInput) ? dateInput.toDate() : dateInput);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    return age < 0 ? 0 : age;
}

export const getVarcharEight = () =>
    Array.apply(0, Array(8))
        .map(() => ((charset) => charset.charAt(Math.floor(Math.random() * charset.length)))("ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"))
        .join("");

export function snapToGrid(x: number, y: number) {
    const snappedX = Math.round(x / 8) * 8;
    const snappedY = Math.round(y / 8) * 8;
    return [snappedX, snappedY];
}

export function blobFromUri(data: Blob | string): Blob {
    if (typeof data === "string") {
        const parts = data.split(";base64,");
        const contentType = parts[0];
        const base64 = atob(parts[1]);
        const array = new Uint8Array(base64.length);

        for (let idx = 0; idx < base64.length; idx++) {
            array[idx] = base64.charCodeAt(idx);
        }

        return new Blob([array.buffer], { type: contentType });
    }
    return data;
}

export async function parseCSVFile(csvFile: File) {
    return new Promise<string[][]>((resolve, reject) => {
        CSVParser.parse(csvFile, {
            skipEmptyLines: "greedy",
            complete: (results) => {
                if (results.errors.length) {
                    reject(results.errors.map((item) => item.message).join(","));
                } else {
                    resolve(results.data as string[][]);
                }
            },
        });
    });
}

export function reorderArray<T extends { [k: string]: any }>(array: T[], colName: string, order: string[]) {
    const secondArray = Array.from(array);
    const result = [];
    for (let i = 0; i < order.length; i++) {
        for (let j = 0; j < secondArray.length; j++) {
            if (secondArray[j][colName] === order[i]) {
                result.push(secondArray.splice(j, 1)[0]);
                break;
            }
        }
    }

    return result;
}

export function getCurrentAUFinancialYear(): [moment.Moment, moment.Moment] {
    const date = new Date();
    const startDate: Date = new Date();
    startDate.setUTCMonth(6);
    startDate.setUTCDate(1);
    startDate.setUTCHours(0, 0, 0);

    const endDate: Date = new Date();
    endDate.setUTCMonth(5);
    endDate.setUTCDate(30);
    endDate.setUTCHours(0, 0, 0);

    const monthNumber = date.getUTCMonth() + 1;

    if (monthNumber >= 7) {
        startDate.setUTCFullYear(date.getUTCFullYear());
        endDate.setUTCFullYear(date.getUTCFullYear() + 1);
    } else {
        startDate.setUTCFullYear(date.getUTCFullYear() - 1);
        endDate.setUTCFullYear(date.getUTCFullYear());
    }

    return [moment(startDate), moment(endDate)];
}
